import { zodResolver } from '@hookform/resolvers/zod'
import { FolderPlus, FolderTree, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'


import { Button, type ButtonProps } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { cn } from '@/utils/cn'

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
})

type FormData = z.infer<typeof schema>

interface CreateGroupProps {
  parentId?: string
  buttonLabel?: string
  buttonVariant?: ButtonProps['variant']
  buttonClassName?: string
  iconOnly?: boolean
}

export function CreateGroup({ parentId, buttonLabel, buttonVariant, buttonClassName, iconOnly = false }: CreateGroupProps) {
  const { recordGroups, create, isCreating } = useRecordGroups()
  const [isOpen, setIsOpen] = useState(false)
  const parentGroup = parentId ? recordGroups.find((group) => group.id === parentId) : undefined
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (!isOpen) reset({ name: '' })
  }, [isOpen, reset])

  const onSubmit = (data: FormData) => {
    create(
      { name: data.name, parentRecordGroupId: parentId },
      {
        onSuccess: () => {
          toast.success('Record group created')
          reset({ name: '' })
          setIsOpen(false)
        },
        onError: () => toast.error('Failed to create record group'),
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant={buttonVariant}
          size={iconOnly ? 'icon' : undefined}
          className={cn('shrink-0 gap-2', buttonClassName)}
          aria-label={iconOnly ? buttonLabel ?? 'Add record group' : undefined}
          title={iconOnly ? buttonLabel ?? 'Add record group' : undefined}
        >
          {iconOnly ? <FolderPlus className="size-4" aria-hidden="true" /> : (
            <>
              <Plus className="h-4 w-4" />
              <span className="sm:hidden">New</span>
              <span className="hidden sm:inline">{buttonLabel ?? 'New Record Group'}</span>
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden p-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader className="flex-row items-center gap-3 px-6 py-4 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <FolderTree className="h-5 w-5" aria-hidden="true" />
            </div>
            <DialogTitle className="text-xl">Create record group</DialogTitle>
          </DialogHeader>

          <div className="space-y-2 px-6">
            {parentId && (
              <div className="space-y-2">
                <Label>Parent group</Label>
                <div className="flex min-h-10 items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm text-foreground">
                  <span className="truncate">{parentGroup?.name ?? 'Selected parent group'}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Label htmlFor="record-group-name">Group name</Label>
              <Input
                id="record-group-name"
                placeholder="e.g. Leads, Contacts, Accounts"
                autoFocus
                {...register('name')}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'record-group-name-error' : undefined}
              />
              {errors.name ? (
                <p id="record-group-name-error" className="text-sm text-destructive">{errors.name.message}</p>
              ) : null}
            </div>
          </div>

          <DialogFooter className="flex-col-reverse gap-2 px-6 pb-5 pt-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating} className="gap-2">
              {isCreating ? 'Creating...' : <><Plus className="h-4 w-4" />Create group</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
